import { useParams, useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import moment from "moment";
import { formatAsMoney } from "../../helpers/numbers";
import { HashLoader } from "react-spinners";
import {
  ArrowCounterClockwise,
  CaretLeft,
  CheckSquare,
  Star,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import useOrderItemDetails from "../../hooks/useOrderItemDetails";
import useMarkOrderItemReceived from "../../hooks/useMarkOrderItemReceived";
import { colorVariants, getOrderStatusMeta } from "../../helpers/colors";

interface TrackingEntry {
  status?: string;
  status_label?: string;
  location?: string | null;
  notes?: string | null;
  created_at?: string;
  created_at_human?: string;
}

export default function OrderItem() {
  const { itemId, orderId } = useParams();
  const navigate = useNavigate();

  const { orderItem, isLoading } = useOrderItemDetails(itemId!);
  const { markReceived, markingReceived } = useMarkOrderItemReceived(itemId!, {
    onSuccess() {
      toast.success("Item marked as received.");
    },
    onError(e) {
      toast.error(e?.message || "Could not mark item as received.");
    },
  });

  if (isLoading) {
    return (
      <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
        <HashLoader />
      </div>
    );
  }

  const item = orderItem?.data;
  if (!item) {
    return (
      <div className="p-5 aspect-video flex items-center justify-center">
        Order item not found
      </div>
    );
  }

  const statusMeta = getOrderStatusMeta(item.status);

  const variant = colorVariants[statusMeta.color] || colorVariants.gray;

  return isLoading ? (
    <div className="grid place-items-center p-20">
      <HashLoader size={40} />
    </div>
  ) : (
    <div className="flex flex-col items-start gap-5 px-5 py-5 pb-20">
      {/* Header */}
      <div className="flex items-center gap-2 p-4 bg-white">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
        >
          <CaretLeft weight="bold" />
          <span>Back</span>
        </button>
        <h1 className="text-lg font-medium ml-4">Order Item</h1>
      </div>

      <div className="flex-1 overflow-y-auto px-4 gap-10 flex flex-col md:flex-row w-full">
        <div className="flex-1 flex flex-col gap-4">
          <div>
            {/* Product row */}
            <div className="flex items-center gap-4 bg-white p-4 rounded-lg border">
              <img
                src={item.product?.images?.[0]?.path}
                alt={item.product?.name || "Product Image"}
                className="w-16 h-16 rounded-md object-cover bg-gray-200"
              />
              <div className="flex-1">
                <div className="font-medium truncate">
                  {item.product?.name || "Could not fetch product name"}
                </div>
                <div className="text-sm opacity-70">
                  Qty: {item?.quantity || "Could not fetch quantity"}
                </div>
              </div>
              <div className="font-medium text-lg">
                ₦ {formatAsMoney(parseFloat(item?.subtotal || "0"))}
              </div>
            </div>
          </div>

          {item.status !== "completed" && (
            <div
              className={`flex gap-3 rounded-xl p-4 items-center ${variant.container}`}
            >
              <div className={variant.iconWrapper}>
                <statusMeta.icon size={24} className={variant.icon} />
              </div>
              <div>
                <div className="flex-1 capitalize font-medium">
                  {statusMeta.label}
                </div>
                <div>{statusMeta.description}</div>
              </div>
            </div>
          )}
        </div>

        <div className="gap-y-6 flex w-full flex-col flex-1">
          <TrackingHistory history={(item.tracking_history as TrackingEntry[]) || []} />

          {/* Rating and actions */}
          <div className="border-t pt-4 space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex flex-col gap-2">
                <div>Rate this product</div>
                <StarRating />
              </div>
              <Link
                to={`/payments/orders/${orderId}/item/${item.id}/review`}
                className="underline"
              >
                Leave a review
              </Link>
            </div>

            <div className="flex justify-between items-center border-t pt-4">
              <button
                onClick={() => markReceived()}
                disabled={markingReceived || item.status !== "delivered"}
                title={
                  item.status !== "delivered" && item.status !== "received"
                    ? "Available once your item is delivered"
                    : undefined
                }
                className="flex items-center gap-2 underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
              >
                <CheckSquare size={18} />
                {markingReceived
                  ? "Marking..."
                  : item.status === "received"
                  ? "Received"
                  : "Mark as Received"}
              </button>
              <button
                onClick={() =>
                  navigate(`/payments/orders/${orderId}/item/${item.id}/return`)
                }
                className="flex items-center gap-2 underline"
              >
                <ArrowCounterClockwise size={18} />
                Return Item
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrackingHistory({ history }: { history: TrackingEntry[] }) {
  // Newest first for the timeline.
  const data = [...history].reverse();

  return (
    <div className="space-y-3">
      <div className="font-medium text-xl">Tracking History:</div>
      {data.length === 0 ? (
        <div className="text-sm opacity-70">No tracking updates yet.</div>
      ) : (
        <div className="flex flex-col">
          {data.map((entry, index) => {
            const timestamp = entry.created_at || null;
            const isLatest = index === 0;
            return (
              <div className="flex gap-3" key={index}>
                <div className="w-28 text-sm">
                  {entry.created_at_human ||
                    (timestamp ? moment(timestamp).calendar() : "")}
                </div>
                <div className="flex flex-col items-center self-stretch">
                  <div
                    className={`w-4 h-4 rounded-full border ${
                      isLatest
                        ? "border-orange-500 bg-orange-500"
                        : "border-orange-300 bg-orange-300"
                    }`}
                  ></div>
                  {index !== data.length - 1 && (
                    <div className="flex-1 h-full w-px bg-orange-300" />
                  )}
                </div>
                <div className="flex-1 pb-5">
                  <div
                    className={`font-medium ${
                      isLatest ? "text-orange-500" : ""
                    }`}
                  >
                    {entry.status_label || entry.status}
                  </div>
                  {entry.location && (
                    <div className="text-sm opacity-70">{entry.location}</div>
                  )}
                  {entry.notes && (
                    <div className="text-sm opacity-70">{entry.notes}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function StarRating({
  initialRating = 0,
  maxStars = 5,
  size = 24,
  color = "#f97316",
  onRate,
}: {
  initialRating?: number;
  maxStars?: number;
  size?: number;
  color?: string;
  onRate?: (rating: number) => void;
}) {
  const [rating, setRating] = useState(initialRating);

  const handleClick = (index: number) => {
    const newRating = index + 1;
    setRating(newRating);
    onRate?.(newRating);
  };

  return (
    <div className="flex gap-1">
      {Array.from({ length: maxStars }).map((_, index) => (
        <button key={index} onClick={() => handleClick(index)}>
          <Star
            size={size}
            weight={index < rating ? "fill" : "regular"}
            color={color}
          />
        </button>
      ))}
    </div>
  );
}
